import * as config from '../config.js';
import * as currency from '../../util/currency.js';
import * as enumtool from '../../util/enumtool.js';
import * as event_mod from '../event.js';
import { push_data_object } from '../../datalayer/gtm/gtm_push.js';
import { EventKind as SelectEvent } from '../../sponsorship/select.js';
import { dispatch as event_dispatch, EventKind } from '../event.js';
import { init_datalayer, push_utag_data } from '../init.js';

// ===========================================================================
// Globals
// ===========================================================================

// Module name
export const MODULE_NAME = 'datalayer_spsh';

// Re-exports
export let global_vars = {};

// Datalayer values
const SPONSORSHIP = {
    age: [],
    brand: [],
    category: [],
    country: [],
    gender: [],
    product_id: [],
    list_name: [],
    list_position: [],
    name: [],
    price: [],
    quantity: [],
    sku: [],
    variant_id: [],

    promo_id: [],
    promo_name: [],
    promo_creative: [],
    promo_position: [],
};

function clear_data() {
    for (let key in SPONSORSHIP) {
        // Only clear arrays of non-promo properties
        if (!key.startsWith('promo')) {
            SPONSORSHIP[key].length = 0;
        }
    }
}

// ===========================================================================
// Helpers
// ===========================================================================

function in_viewport(global, el) {
    let rect = el.getBoundingClientRect();
    let doc = global.document;

    return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <=
            (global.innerHeight || doc.documentElement.clientHeight) &&
        rect.right <= (global.innerWidth || doc.documentElement.clientWidth)
    );
}

function active_slide(global, slide, position) {
    let slide_data = slide.firstChild.dataset;

    SPONSORSHIP.age.push(slide_data.dataAge);
    SPONSORSHIP.brand.push('Pledge');
    SPONSORSHIP.category.push(`Sponsorship/${slide_data.country}`);
    SPONSORSHIP.country.push(slide_data.country);
    SPONSORSHIP.gender.push(slide_data.gender);
    SPONSORSHIP.product_id.push(slide_data.productId);
    SPONSORSHIP.list_name.push('sponsorship');
    SPONSORSHIP.list_position.push(position);
    SPONSORSHIP.name.push('Child Sponsorship');
    SPONSORSHIP.price.push(currency.clean(slide_data.price).toString());
    SPONSORSHIP.quantity.push(1);
    SPONSORSHIP.sku.push(slide_data.sku);
    SPONSORSHIP.variant_id.push(slide_data.variantId);
}

function track_active_slides(global) {
    let all_slides = global.document.querySelectorAll('.sp-slide');
    for (let i = 0; i++; i < all_slides.length) {
        let slide = all_slides[i];
        if (in_viewport(slide)) {
            // The position argument is 1-based
            active_slide(global, slide, i + 1);
        }
    }
}

// ===========================================================================
// Data layer handlers
// ===========================================================================

function onclick_slider_arrow(global) {
    let buttons = global.document.querySelectorAll('.sp-arrow');

    for (let button of buttons) {
        button.addEventListener('click', () => {
            // This clears all data from properties of SPONSORSHIP object
            clear_data();

            track_active_slides(global);
            event_mod.dispatch(global, EventKind.Impression);
        });
    }
}

function store_slide_data(global, utag_data, slide) {
    let slide_data = slide.dataset;
    let country = slide_data.country;

    utag_data['event_category'] = 'Ecommerce';
    utag_data['event_action'] = 'Product Click';
    utag_data['event_label'] = 'Sponsorship Product Click';

    utag_data['product_price'] = currency.clean(slide_data.price).toString();
    utag_data['product_id'] = slide_data.productId;
    utag_data['product_name'] = 'Child Sponsorship';
    utag_data['product_brand'] = 'Pledge';
    utag_data['product_variant'] = slide_data.variantId;
    utag_data['product_category'] = `Sponsorship/${country}`;
    utag_data['product_quantity'] = '1';
    utag_data['product_list'] = 'sponsorship';
    utag_data['product_position'] = '1';
    utag_data['product_age'] = slide_data.age;
    utag_data['product_gender'] = slide_data.gender;
    utag_data['product_country'] = country;
    utag_data['enh_action'] = 'click';

    //== gtm-push: OSP Card Clicked (Product Click) ==//
    let data_object = {
        event: 'select_item',
        ecommerce: {
            items: [
                {
                    item_name: 'Child Sponsorship',
                    item_id: slide_data.productId,
                    item_brand: 'Pledge',
                    price: currency.clean(slide_data.price).toString(),
                    item_list_name: 'Sponsorship',
                    item_category: `Sponsorship/${country}`,
                    item_variant: slide_data.variantId,
                    index: '1',
                },
            ],
        },
    };
    push_data_object(data_object);

    //== gtm-push: OSP Card Clicked (Product View) ==//
    data_object = {
        event: 'view_item',
        ecommerce: {
            items: [
                {
                    item_name: 'Child Sponsorship',
                    item_id: slide_data.productId,
                    item_brand: 'Pledge',
                    price: currency.clean(slide_data.price).toString(),
                    item_category: `Sponsorship/${country}`,
                    item_variant: slide_data.variantId,
                    item_list_name: 'Sponsorship',
                    index: '1',
                },
            ],
        },
    };
    push_data_object(data_object);

    event_dispatch(global, EventKind.ProductClick);
}

function onclick_slider_slide(global, utag_data) {
    let event_type = enumtool.enum_value(SelectEvent.SlideSelected);
    global.document.addEventListener(event_type, event => {
        store_slide_data(global, utag_data, event.detail.slide);
    });
}

function store_button_data(global, utag_data, button) {
    let button_data = button.dataset;
    let suffix = button_data.datalayer_suffix;
    let country = button_data.country;

    utag_data['event_category'] = 'Ecommerce';
    utag_data['event_action'] = 'Add to Cart';
    utag_data['event_label'] = `Sponsorship Added To Cart${suffix}`;

    utag_data['product_price'] = currency.clean(button_data.amount).toString();
    utag_data['product_id'] = button_data.productid;
    utag_data['product_name'] = 'Child Sponsorship';
    utag_data['product_brand'] = 'Pledge';
    utag_data['product_variant'] = button_data.variant;
    utag_data['product_category'] = `Sponsorship/${country}`;
    utag_data['product_quantity'] = '1';
    utag_data['product_list'] = 'Selected Sponsorship';
    utag_data['product_position'] = '1';
    utag_data['product_age'] = button_data.age;
    utag_data['product_gender'] = button_data.gender;
    utag_data['product_country'] = country;

    //== gtm-push: OSP Child added to cart ==//
    let data_object = {
        event: 'add_to_cart',
        ecommerce: {
            items: [
                {
                    item_name: 'Child Sponsorship',
                    item_id: button_data.productid,
                    price: currency.clean(button_data.amount).toString(),
                    item_brand: 'Pledge',
                    item_category: `Sponsorship/${country}`,
                    item_variant: button_data.variant,
                    item_list_name: 'Sponsorship',
                    quantity: '1',
                },
            ],
        },
    };
    push_data_object(data_object);

    event_dispatch(global, EventKind.AddToCart);
}

// Used by the events app
export function onclick_spsh_button(global, utag_data, tocart_event) {
    global.document.addEventListener(tocart_event.type, () => {
        let data = global.document.querySelector('#sponsor_data');
        store_button_data(global, utag_data, data);
    });
}

// Used by the osp home page
export function on_sponsorship_added_to_cart(global, utag_data) {
    let data = global.document.querySelector('#sponsor_data');
    store_button_data(global, utag_data, data);
}

// ===========================================================================
// Data layer
// ===========================================================================

// global is the window object when in the browser context
// this function should be called only after init_data() function is called
function update_data(global, utag_data, url, title) {
    let lang = global.document.querySelector('body').dataset.lang;

    // url should be the value of window.location.href
    // title should be the value of document.title

    // These were not in the new document, so we're keeping
    // them around in case they're still being used.
    utag_data['impression_age'] = SPONSORSHIP.age;
    utag_data['impression_brand'] = SPONSORSHIP.brand;
    utag_data['impression_country'] = SPONSORSHIP.country;
    utag_data['impression_gender'] = SPONSORSHIP.gender;
    utag_data['impression_sku'] = SPONSORSHIP.sku;
    utag_data['page_url'] = url;
    utag_data['device_type'] = config.DEVICE.device;
    utag_data['language'] = `${lang}-CA`;

    // These carried over from the old code
    utag_data['page_type'] = 'sponsor';
    utag_data['page_name'] = title;

    // New
    utag_data['page_category_sbu'] = 'OSP';
    utag_data['impression_id'] = SPONSORSHIP.product_id;

    // These will always be empty arrays, cause sponsorship products
    // will never be considered promotions
    utag_data['promo_imp_ids'] = SPONSORSHIP.promo_id;
    utag_data['promo_imp_names'] = SPONSORSHIP.promo_name;
    utag_data['promo_imp_creatives'] = SPONSORSHIP.promo_creative;
    utag_data['promo_imp_positions'] = SPONSORSHIP.promo_position;
    utag_data['impression_name'] = SPONSORSHIP.name;
    utag_data['impression_category'] = SPONSORSHIP.category;
    utag_data['impression_variant'] = SPONSORSHIP.variant_id;
    utag_data['impression_price'] = SPONSORSHIP.price;
    utag_data['impression_list'] = SPONSORSHIP.list_name;
    utag_data['impression_position'] = SPONSORSHIP.list_position;

    push_utag_data(utag_data);
}

function init_handlers(global, data) {
    // Setup data layer handlers
    onclick_slider_arrow(global);
    onclick_slider_slide(global, data.utag_data);
    // onclick_spsh_button(global, data.utag_data);
}

// global is the window object when in the browser context this function should
export function initialize(global) {
    init_datalayer(global, init_handlers, update_data);
}

// ===========================================================================
//
// ===========================================================================
